import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { SparklesIcon, PhoneIcon, MapPinIcon, UserIcon, Share2Icon, BarChart3Icon, ZapIcon, ArrowRightIcon } from 'lucide-react';
const basicFields = ['Business name', 'Full address', 'Phone number', 'Company email', 'Website URL', 'Business category', 'Latitude & longitude', 'Business hours', 'Opening status', 'Place ID'];
const enrichedCategories = [{
  label: 'Contacts',
  icon: UserIcon,
  fields: ['Decision maker name', 'Owner email', 'Owner phone']
}, {
  label: 'Social',
  icon: Share2Icon,
  fields: ['Facebook page', 'Instagram handle', 'Twitter/X account', 'LinkedIn company', 'Social media profiles']
}, {
  label: 'Business Intel',
  icon: BarChart3Icon,
  fields: ['Google rating & reviews', 'Review count', 'Popular times', 'Business description', 'Price range', 'Photos count', 'Street view']
}, {
  label: 'Advanced Signals',
  icon: ZapIcon,
  fields: ['Verified status', 'Revenue estimate', 'Employee size', 'Tech stack used', 'Ad spend signals']
}];
const containerVariants = {
  hidden: {
    opacity: 0
  },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.03
    }
  }
};
const chipVariants = {
  hidden: {
    opacity: 0,
    scale: 0.9
  },
  show: {
    opacity: 1,
    scale: 1,
    transition: {
      duration: 0.3
    }
  }
};
export function DataFields() {
  return <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-24" data-id="element-2652">
      <div className="bg-white rounded-[2.5rem] shadow-sm border border-slate-100 p-8 md:p-12 lg:p-16 relative overflow-hidden" data-id="element-2653">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16 relative z-10" data-id="element-2654">
          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-50px'
        }} transition={{
          duration: 0.5
        }} className="text-3xl md:text-4xl font-bold mb-4 text-slate-900" data-id="element-2655">
            Comprehensive data points &{' '}
            <span className="text-blue-600" data-id="element-2656">enrichments</span>
          </motion.h2>
          <motion.p initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-50px'
        }} transition={{
          duration: 0.5,
          delay: 0.1
        }} className="text-lg text-slate-500" data-id="element-2657">
            Get complete business intelligence with 40+ data points per record
          </motion.p>
        </div>

        <div className="grid lg:grid-cols-5 gap-8 relative z-10" data-id="element-2658">
          {/* Left Column: Basic Contact Data */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-50px'
        }} transition={{
          duration: 0.5,
          delay: 0.2
        }} className="lg:col-span-2 bg-white rounded-2xl border border-slate-200 p-6 md:p-8" data-id="element-2659">
            <div className="flex items-center gap-3 mb-2" data-id="element-2660">
              <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center" data-id="element-2661">
                <PhoneIcon className="w-5 h-5 text-slate-500" data-id="element-2662" />
              </div>
              <div data-id="element-2663">
                <h3 className="text-lg font-bold text-slate-900" data-id="element-2664">
                  Basic contact data
                </h3>
                <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider" data-id="element-2665">
                  Included
                </span>
              </div>
            </div>

            <div className="h-px bg-slate-100 my-5" data-id="element-2666" />

            <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
            once: true,
            margin: '-50px'
          }} className="flex flex-wrap gap-2.5" data-id="element-2667">
              {basicFields.map((field, idx) => <motion.span key={idx} variants={chipVariants} className="px-3.5 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm font-medium text-slate-700 hover:bg-slate-100 hover:border-slate-300 transition-colors cursor-default" data-id="element-2668">
                  {field}
                </motion.span>)}
            </motion.div>

            <div className="mt-6 pt-5 border-t border-slate-100" data-id="element-2669">
              <div className="text-xs font-bold text-slate-400 uppercase tracking-wider" data-id="element-2670">
                {basicFields.length} data points
              </div>
            </div>
          </motion.div>

          {/* Right Column: Enriched Intelligence */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-50px'
        }} transition={{
          duration: 0.5,
          delay: 0.3
        }} className="lg:col-span-3 bg-slate-900 rounded-2xl p-6 md:p-8 relative overflow-hidden" data-id="element-2671">
            <div className="flex items-center gap-3 mb-2 relative z-10" data-id="element-2672">
              <div className="w-10 h-10 rounded-xl bg-blue-500/20 flex items-center justify-center" data-id="element-2673">
                <SparklesIcon className="w-5 h-5 text-blue-400" data-id="element-2674" />
              </div>
              <div data-id="element-2675">
                <h3 className="text-lg font-bold text-white flex items-center gap-2" data-id="element-2676">
                  Enriched intelligence
                  <motion.span animate={{
                  opacity: [1, 0.3, 1],
                  scale: [1, 1.1, 1]
                }} transition={{
                  duration: 1.5,
                  repeat: Infinity,
                  ease: 'easeInOut'
                }} className="bg-blue-500/20 text-blue-400 text-[10px] px-2 py-0.5 rounded-full font-bold uppercase tracking-wider" data-id="element-2677">
                    Premium
                  </motion.span>
                </h3>
                <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider" data-id="element-2678">
                  30+ enriched fields
                </span>
              </div>
            </div>

            <div className="h-px bg-slate-800 my-5" data-id="element-2679" />

            <div className="space-y-6 relative z-10" data-id="element-2680">
              {enrichedCategories.map((category, catIdx) => {
              const Icon = category.icon;
              return <motion.div key={catIdx} initial={{
                opacity: 0,
                y: 15
              }} whileInView={{
                opacity: 1,
                y: 0
              }} viewport={{
                once: true,
                margin: '-50px'
              }} transition={{
                duration: 0.4,
                delay: 0.3 + catIdx * 0.1
              }} data-id="element-2681">
                    <div className="flex items-center gap-2 mb-3" data-id="element-2682">
                      <Icon className="w-3.5 h-3.5 text-blue-400" data-id="element-2683" />
                      <span className="text-xs font-bold text-slate-500 uppercase tracking-wider" data-id="element-2684">
                        {category.label}
                      </span>
                    </div>
                    <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
                  once: true,
                  margin: '-50px'
                }} className="flex flex-wrap gap-2" data-id="element-2685">
                      {category.fields.map((field, idx) => <motion.span key={idx} variants={chipVariants} className="px-3.5 py-2 bg-slate-800/80 border border-slate-700/50 rounded-xl text-sm font-medium text-slate-300 hover:bg-slate-700/80 hover:border-blue-500/30 hover:text-blue-300 transition-all cursor-default" data-id="element-2686">
                          {field}
                        </motion.span>)}
                    </motion.div>
                  </motion.div>;
            })}
            </div>
          </motion.div>
        </div>

        {/* Bottom Badge */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-50px'
      }} transition={{
        duration: 0.5,
        delay: 0.6
      }} className="mt-12 flex justify-center relative z-10" data-id="element-2687">
          <motion.div animate={{
          boxShadow: ['0 10px 15px -3px rgba(37,99,235,0.2)', '0 10px 25px -3px rgba(37,99,235,0.4)', '0 10px 15px -3px rgba(37,99,235,0.2)']
        }} transition={{
          duration: 3,
          repeat: Infinity,
          ease: 'easeInOut'
        }} className="inline-flex items-center gap-2 px-8 py-3.5 bg-blue-600 text-white rounded-full font-bold shadow-lg shadow-blue-600/20 hover:-translate-y-0.5 transition-all cursor-default text-lg" data-id="element-2688">
            40+ total data points available
          </motion.div>
        </motion.div>
      </div>
    </section>;
}